import {
  CalendarDots,
  CaretDown,
  ClockCounterClockwise,
  Play,
} from "@phosphor-icons/react";
import { clsx } from "clsx";
import { useState } from "react";

import RenderIf from "common/components/RenderIf";
import RestrictedComponent from "common/components/RestrictedComponent";
import { Button } from "common/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import { formatBillingDate } from "../../../../workspace-settings/utils";
import { CAMPAIGN_OUTREACH_OPTIONS } from "../../../constants";
import { useCampaignContext } from "../../../context/CampaignContext";
import useCampaignValidation from "../../../hooks/useCampaignValidation";
import {
  CampaignActionsDialog,
  CampaignActionsDropdown,
} from "../../CampaignActions";
import CampaignStateTag from "../../CampaignStateTag";
import WorkerAccounts from "../../WorkerAccounts";
import WorkerAccountsTag from "../../WorkerAccountsTag";

import type { CampaignActionType } from "../../../types";

export default function CampaignInfo() {
  const isDesktop = useTwBreakpoint("lg");
  const campaign = useCampaignContext();

  const {
    name,
    state,
    outreach_type: outreachType,
    owners,
    start_date: startDate,
    uses_connection_note: isUsingNote,
    created_at: createdAt,
  } = campaign;
  const isDrip = outreachType === "drip";
  const { icon: SubtypeIcon } = CAMPAIGN_OUTREACH_OPTIONS[outreachType];

  const [selectedActionType, setSelectedActionType] =
    useState<CampaignActionType>();
  const [isActionsModalOpen, setIsActionsModalOpen] = useState(false);

  function setAction(action: CampaignActionType) {
    setIsActionsModalOpen(true);
    setSelectedActionType(action);
  }

  const { hasInsufficientLicenses } = useCampaignValidation(owners);

  const workerAccounts = isDrip ? (
    <WorkerAccountsTag size="md" owners={owners} isUsingNote={isUsingNote} />
  ) : (
    <WorkerAccounts />
  );

  const stateTag = (
    <CampaignStateTag
      size="md"
      state={state}
      owners={owners}
      startDate={startDate}
      isUsingNote={isUsingNote}
    />
  );
  const typeAndSubtype = (
    <div className="flex lg:items-center gap-1 lg:gap-x-2 lg:gap-y-0.5 text-black-500 text-button-xs flex-wrap flex-col lg:flex-row">
      <div className="flex items-center gap-x-1">
        <SubtypeIcon size={16} />
        <span className="line-clamp-1 break-all">
          {isDrip ? "1st" : "2nd & 3rd+"} connections
        </span>
      </div>
      <div className="flex items-center gap-x-1">
        <CalendarDots size={16} />
        Created {formatBillingDate(createdAt)}
      </div>
    </div>
  );

  const startCampaignButton = (
    <RestrictedComponent visibleFor={["campaigns_full"]}>
      <RenderIf condition={state === "PAUSED" || state === "SCHEDULED"}>
        <Button
          variant="default-purple"
          leftIcon={<Play weight="fill" />}
          size="sm"
          onClick={() =>
            setAction(hasInsufficientLicenses ? "license" : "start")
          }
        >
          Start
        </Button>
      </RenderIf>

      <RenderIf condition={state === "CREATED"}>
        <div className="group flex rounded-full bg-purple-500">
          <button
            type="button"
            className="flex grow items-center justify-center gap-2 rounded-l-full pl-3 pr-2 text-button-xs text-white transition-colors duration-300 hover:bg-purple-400"
            onClick={() =>
              setAction(hasInsufficientLicenses ? "license" : "start")
            }
          >
            Start campaign
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger
              aria-label="more-start-campaign-options"
              className={clsx(
                "group relative h-8 rounded-r-full p-2 transition-colors duration-300 data-[state=open]:bg-purple-400 hover:bg-purple-400",
                "before:absolute before:left-0 before:top-1 before:h-6 before:w-px before:bg-purple-400 before:opacity-100 before:transition-opacity before:duration-300 before:ease-in-out before:group-hover:opacity-0",
              )}
            >
              <CaretDown className="size-4 fill-white transition-transform group-data-[state=open]:rotate-180" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuList>
                <DropdownMenuItem
                  onClick={() =>
                    setAction(hasInsufficientLicenses ? "license" : "schedule")
                  }
                >
                  <ClockCounterClockwise />
                  Schedule campaign
                </DropdownMenuItem>
              </DropdownMenuList>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </RenderIf>
    </RestrictedComponent>
  );
  const actionsDropdown = (
    <RestrictedComponent visibleFor={["campaigns_full"]}>
      <CampaignActionsDropdown campaign={campaign} isDetailedView />
    </RestrictedComponent>
  );

  let mainContent: JSX.Element;
  if (isDesktop) {
    mainContent = (
      <>
        <div className="flex flex-col gap-y-0.5">
          <div className="flex items-center gap-x-1.5">
            <h2 className="break-words text-md-bold">{name}</h2>
            {stateTag}
          </div>

          {typeAndSubtype}
        </div>

        <div className="flex flex-1 items-center justify-end gap-x-2.5">
          <div className="flex items-center gap-x-1">
            <span className="text-xs text-black-500">Run by:</span>
            {workerAccounts}
          </div>
          <span className="h-8 w-px bg-black-200 last:hidden" />
          {startCampaignButton}
          {actionsDropdown}
        </div>
      </>
    );
  } else {
    mainContent = (
      <>
        <div className="flex w-full gap-x-2.5">
          <div className="flex grow flex-col gap-y-1">
            <h2 className="break-words text-md-bold">{name}</h2>
            {typeAndSubtype}
          </div>

          <span className="h-8 w-px bg-black-200 last:hidden" />
          {actionsDropdown}
        </div>

        {/* Divider */}
        <span className="my-2 h-px w-full bg-black-200" />

        <div className="flex w-full items-center justify-between gap-x-2">
          <div className="flex items-center gap-x-1">
            <span className="text-xs text-black-500">Run by:</span>
            {workerAccounts}
          </div>

          {stateTag}
        </div>
      </>
    );
  }

  return (
    <>
      <section className="flex flex-col items-start rounded-3xl border border-black-200 p-3 lg:flex-row lg:items-center lg:flex-wrap lg:gap-2.5">
        {mainContent}
      </section>

      <RenderIf condition={!isDesktop}>{startCampaignButton}</RenderIf>

      <CampaignActionsDialog
        isOpen={isActionsModalOpen}
        setIsOpen={setIsActionsModalOpen}
        actionType={selectedActionType}
        campaign={campaign}
      />
    </>
  );
}
